import type { FC, PropsWithChildren } from 'react';

import Skeleton from './third_party/skeleton';
import Typography from './third_party/typography';

interface IProps {
  [x: string]: unknown;
  width?: string | number;
  preload?: boolean;
  color?: string;
  className?: string;
  component?: string;
}

export const H1: FC<PropsWithChildren<IProps>> = ({ width, preload, className, color = 'textPrimary', ...rest }) => {
  return preload ? (
    <Skeleton width={width} height={43} variant="text" className={className} />
  ) : (
    <Typography height={43} width={width} variant="h1" component="div" color={color} className={className} {...rest} />
  );
};

export const H2: FC<PropsWithChildren<IProps>> = ({ width, preload, className, color = 'textPrimary', ...rest }) => {
  return preload ? (
    <Skeleton width={width} height={43} variant="text" className={className} />
  ) : (
    <Typography height={43} width={width} variant="h2" component="div" color={color} className={className} {...rest} />
  );
};

export const H3: FC<PropsWithChildren<IProps>> = ({ width, preload, className, color = 'textPrimary', ...rest }) => {
  return preload ? (
    <Skeleton width={width} height={29} variant="text" className={className} />
  ) : (
    <Typography height={29} width={width} variant="h3" component="div" color={color} className={className} {...rest} />
  );
};

export const H4: FC<PropsWithChildren<IProps>> = ({ width, preload, className, color = 'textPrimary', ...rest }) => {
  return preload ? (
    <Skeleton width={width} height={24} variant="text" className={className} />
  ) : (
    <Typography height={24} width={width} variant="h4" color={color} component="div" className={className} {...rest} />
  );
};

export const H5: FC<PropsWithChildren<IProps>> = ({ width, preload, className, color = 'textPrimary', ...rest }) => {
  return preload ? (
    <Skeleton width={width} height={22} variant="text" className={className} />
  ) : (
    <Typography height={22} width={width} component="div" variant="h5" color={color} className={className} {...rest} />
  );
};

export const H6: FC<PropsWithChildren<IProps>> = ({ width, preload, className, color = 'textPrimary', ...rest }) => {
  return preload ? (
    <Skeleton width={width} height={17} variant="text" className={className} />
  ) : (
    <Typography width={width} height={17} component="div" variant="h6" color={color} className={className} {...rest} />
  );
};

export const Body1: FC<PropsWithChildren<IProps>> = ({
  width,
  component = 'div',
  preload,
  color = 'textPrimary',
  className,
  ...rest
}) => {
  return preload ? (
    <Skeleton width={width} height={24} variant="text" className={className} />
  ) : (
    <Typography
      height={24}
      width="75%"
      variant="body1"
      component={component}
      color={color}
      className={className}
      {...rest}
    />
  );
};

export const Body2: FC<PropsWithChildren<IProps>> = ({ width, preload, className, color = 'textPrimary', ...rest }) => {
  return preload ? (
    <Skeleton variant="text" width={width} height={17} className={className} />
  ) : (
    <Typography
      width={width}
      height={17}
      component="div"
      variant="body2"
      className={className}
      color={color}
      {...rest}
    />
  );
};

export const SubTitle1: FC<PropsWithChildren<IProps>> = ({
  width,
  preload,
  className,
  component = 'div',
  color = 'textPrimary',
  ...rest
}) => {
  return preload ? (
    <Skeleton width={width} height={28} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={28}
      {...rest}
      color={color}
      variant="subtitle1"
      component={component}
      className={className}
    />
  );
};

export const SubTitle2: FC<PropsWithChildren<IProps>> = ({ width, preload, className, ...rest }) => {
  return preload ? (
    <Skeleton variant="text" width={width} height={22} className={className} />
  ) : (
    <Typography width={width} height={22} component="div" variant="subtitle2" className={className} {...rest} />
  );
};

export const Caption: FC<PropsWithChildren<IProps>> = ({
  width,
  preload,
  color = 'textSecondary',
  className,
  ...rest
}) => {
  return preload ? (
    <Skeleton variant="text" width={width} height={14} className={className} />
  ) : (
    <Typography
      width={width}
      height={14}
      variant="caption"
      component="div"
      color={color}
      className={className}
      {...rest}
    />
  );
};

export const Overline: FC<PropsWithChildren<IProps>> = ({ width, preload, color = 'primary', className, ...rest }) => {
  return preload ? (
    <Skeleton width={width} height={15} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={15}
      component="div"
      variant="overline"
      color={color}
      {...rest}
      className={className}
    />
  );
};

export const TooltipText: FC<PropsWithChildren<IProps>> = ({
  width,
  preload,
  color = 'textSecondary',
  className,
  ...rest
}) => {
  return preload ? (
    <Skeleton width={width} height={16} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={16}
      component="div"
      color={color}
      {...rest}
      className={`${className} text-xs font-normal`}
    />
  );
};

export const ChipText: FC<PropsWithChildren<IProps>> = ({
  width,
  preload,
  color = 'textSecondary',
  className,
  ...rest
}) => {
  return preload ? (
    <Skeleton width={width} height={16} variant="text" className={className} />
  ) : (
    <Typography
      width={width}
      height={16}
      component="div"
      color={color}
      {...rest}
      className={`${className} text-xs font-normal leading-3`}
    />
  );
};

export const CreateMarkup = (x: string) => ({ __html: x });

export const DangerousText: FC<PropsWithChildren<{ str: string; className?: string }>> = ({ str, className }) => {
  return <span className={className} dangerouslySetInnerHTML={CreateMarkup(str)} />;
};
